import { useState, useEffect } from 'react';
import { useStore } from '../store';
import { Template } from '../types';
import {
  X,
  Plus,
  Trash2,
  Copy,
  Search,
  Save,
  FileText,
  GripVertical,
  ChevronRight,
  Loader2,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templates: Template[];
  onSave: (templates: Template[]) => void;
}

export default function TemplateManagerModal({ isOpen, onClose, templates, onSave }: Props) {
  const { addNotification } = useStore();
  const [localTemplates, setLocalTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalTemplates([...templates.map((t) => ({ ...t, sections: [...t.sections.map((s) => ({ ...s }))] }))]);
      setSelectedId(templates.length > 0 ? templates[0].id : null);
      setSearchQuery('');
      setHasChanges(false);
    }
  }, [isOpen, templates]);

  if (!isOpen) return null;

  const selectedTemplate = localTemplates.find((t) => t.id === selectedId);

  const filteredTemplates = localTemplates.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const createTemplate = () => {
    const newTemplate: Template = {
      id: `template-${Date.now()}`,
      name: 'New Template',
      isCustom: true,
      sections: [
        { title: 'Section 1', prompt: 'Enter your prompt here...' },
      ],
    };
    setLocalTemplates((prev) => [...prev, newTemplate]);
    setSelectedId(newTemplate.id);
    setHasChanges(true);
  };

  const deleteTemplate = (id: string) => {
    const template = localTemplates.find((t) => t.id === id);
    if (template?.isSystem) return;

    if (!confirm('Are you sure you want to delete this template?')) return;

    setLocalTemplates((prev) => prev.filter((t) => t.id !== id));
    if (selectedId === id) {
      const remaining = localTemplates.filter((t) => t.id !== id);
      setSelectedId(remaining.length > 0 ? remaining[0].id : null);
    }
    setHasChanges(true);
  };

  const duplicateTemplate = (id: string) => {
    const template = localTemplates.find((t) => t.id === id);
    if (!template) return;

    const duplicate: Template = {
      ...template,
      id: `template-${Date.now()}`,
      name: `${template.name} (Copy)`,
      isCustom: true,
      isSystem: false,
      sections: [...template.sections.map((s) => ({ ...s }))],
    };
    setLocalTemplates((prev) => [...prev, duplicate]);
    setSelectedId(duplicate.id);
    setHasChanges(true);
  };

  const updateTemplateName = (name: string) => {
    if (!selectedId) return;
    setLocalTemplates((prev) =>
      prev.map((t) => (t.id === selectedId ? { ...t, name } : t))
    );
    setHasChanges(true);
  };

  const updateSectionTitle = (index: number, title: string) => {
    if (!selectedId) return;
    setLocalTemplates((prev) =>
      prev.map((t) => {
        if (t.id === selectedId) {
          const sections = [...t.sections];
          sections[index] = { ...sections[index], title };
          return { ...t, sections };
        }
        return t;
      })
    );
    setHasChanges(true);
  };

  const updateSectionPrompt = (index: number, prompt: string) => {
    if (!selectedId) return;
    setLocalTemplates((prev) =>
      prev.map((t) => {
        if (t.id === selectedId) {
          const sections = [...t.sections];
          sections[index] = { ...sections[index], prompt };
          return { ...t, sections };
        }
        return t;
      })
    );
    setHasChanges(true);
  };

  const addSection = () => {
    if (!selectedId) return;
    setLocalTemplates((prev) =>
      prev.map((t) => {
        if (t.id === selectedId) {
          return {
            ...t,
            sections: [
              ...t.sections,
              { title: `Section ${t.sections.length + 1}`, prompt: 'Enter prompt...' },
            ],
          };
        }
        return t;
      })
    );
    setHasChanges(true);
  };

  const removeSection = (index: number) => {
    if (!selectedId) return;
    setLocalTemplates((prev) =>
      prev.map((t) => {
        if (t.id === selectedId && t.sections.length > 1) {
          const sections = t.sections.filter((_, i) => i !== index);
          return { ...t, sections };
        }
        return t;
      })
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      onSave(localTemplates);
      setHasChanges(false);
      addNotification('Templates saved successfully', 'success');
      onClose();
    } catch {
      addNotification('Failed to save templates', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-4xl h-[600px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-accent" />
            <h2 className="text-lg font-semibold">Template Manager</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {localTemplates.length} templates
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary flex items-center gap-1.5 text-sm"
              >
                {isSaving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Save All
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - Template List */}
          <div className="w-64 border-r border-gray-200 flex flex-col shrink-0">
            {/* Search */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                />
              </div>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedId(template.id)}
                  className={`flex items-center justify-between px-3 py-2.5 cursor-pointer border-b border-gray-50 transition-colors ${
                    selectedId === template.id
                      ? 'bg-accent/5 border-l-2 border-l-accent'
                      : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {template.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {template.sections.length} section{template.sections.length !== 1 ? 's' : ''}
                      {template.isSystem ? ' | System' : template.isCustom ? ' | Custom' : ''}
                    </p>
                  </div>
                  {selectedId === template.id && (
                    <ChevronRight size={14} className="text-accent shrink-0" />
                  )}
                </div>
              ))}

              {filteredTemplates.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-400">
                  No templates found
                </div>
              )}
            </div>

            {/* Add Template Button */}
            <div className="p-3 border-t border-gray-200">
              <button
                onClick={createTemplate}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-accent border border-accent/30 rounded-md hover:bg-accent/5 transition-colors"
              >
                <Plus size={14} />
                New Template
              </button>
            </div>
          </div>

          {/* Editor */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedTemplate ? (
              <>
                {/* Template Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between shrink-0">
                  <input
                    type="text"
                    value={selectedTemplate.name}
                    onChange={(e) => updateTemplateName(e.target.value)}
                    disabled={selectedTemplate.isSystem}
                    className="text-base font-semibold bg-transparent border-b border-transparent focus:border-accent outline-none px-1 disabled:text-gray-500 w-full max-w-sm"
                    placeholder="Template name"
                  />
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => duplicateTemplate(selectedTemplate.id)}
                      className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors"
                      title="Duplicate template"
                    >
                      <Copy size={16} />
                    </button>
                    {!selectedTemplate.isSystem && (
                      <button
                        onClick={() => deleteTemplate(selectedTemplate.id)}
                        className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete template"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Sections Editor */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {selectedTemplate.sections.map((section, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-3 bg-white hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <GripVertical size={14} className="text-gray-300 cursor-grab" />
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateSectionTitle(index, e.target.value)}
                          disabled={selectedTemplate.isSystem}
                          className="flex-1 text-sm font-medium bg-transparent border-b border-transparent focus:border-accent outline-none disabled:text-gray-500"
                          placeholder="Section title"
                        />
                        {!selectedTemplate.isSystem && selectedTemplate.sections.length > 1 && (
                          <button
                            onClick={() => removeSection(index)}
                            className="p-1 hover:bg-red-50 rounded text-gray-300 hover:text-red-500 transition-colors"
                            title="Remove section"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                      <textarea
                        value={section.prompt}
                        onChange={(e) => updateSectionPrompt(index, e.target.value)}
                        disabled={selectedTemplate.isSystem}
                        rows={3}
                        className="w-full text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-accent/30 disabled:text-gray-400"
                        placeholder="Section prompt..."
                      />
                    </div>
                  ))}

                  {/* Add Section Button */}
                  {!selectedTemplate.isSystem && (
                    <button
                      onClick={addSection}
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >
                      <Plus size={14} />
                      Add Section
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FileText size={48} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Select a template to edit</p>
                  <p className="text-xs mt-1">or create a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
